import { noop } from '@page/core/utils/function';
import { createContext, useCallback, useMemo, useState } from 'react';

export type ChildPositionContextValue = {
  register: (listItemNode: HTMLElement) => void;
  unregister: (listItemNode: HTMLElement) => void;
  map: Map<HTMLElement, number | null>;
};

export const ChildPositionContext = createContext<ChildPositionContextValue>({
  register: noop,
  unregister: noop,
  map: new Map(),
});

export function ChildPositionProvider({ children }: React.PropsWithChildren): React.JSX.Element {
  const [nodes, setNodes] = useState(() => new Set<HTMLElement>());

  const register = useCallback((node: HTMLElement) => {
    setNodes((prevSet) => new Set(prevSet).add(node));
  }, []);

  const unregister = useCallback((node: HTMLElement) => {
    setNodes((prevSet) => {
      const set = new Set(prevSet);
      set.delete(node);
      return set;
    });
  }, []);

  const map = useMemo(() => {
    const newMap = new Map<HTMLElement, number>();
    const sortedNodes = Array.from(nodes.keys()).sort(sortByDocumentPosition);

    sortedNodes.forEach((node, index) => {
      newMap.set(node, index);
    });

    return newMap;
  }, [nodes]);

  const value = useMemo(() => ({ register, unregister, map }), [register, unregister, map]);

  return <ChildPositionContext.Provider value={value}>{children}</ChildPositionContext.Provider>;
}

function sortByDocumentPosition(a: Node, b: Node) {
  const position = a.compareDocumentPosition(b);

  if (position & Node.DOCUMENT_POSITION_FOLLOWING || position & Node.DOCUMENT_POSITION_CONTAINED_BY) {
    return -1;
  }

  if (position & Node.DOCUMENT_POSITION_PRECEDING || position & Node.DOCUMENT_POSITION_CONTAINS) {
    return 1;
  }

  return 0;
}
